import type { FC, ReactNode } from 'react';
import { useState } from 'react';
import { captureException } from '@sentry/react';
import { BiDuplicate } from 'react-icons/bi';
import { TbBolt, TbCategoryPlus, TbEdit, TbExternalLink, TbLink, TbStar, TbStarOff, TbTrash } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { useLifecycleResourceActions } from '@/hooks/useLifecycleResourceActions';
import { useResourceActionAccess } from '@/hooks/useResourceActionAccess';
import { useWorkspaceResourceAccess } from '@/hooks/useWorkspaceResourceAccess';
import { useCreateLifecycle, useDeleteLifecycle, useUpdateLifecycle } from '@/queries/tanstack/database/entities';
import { lifecycleDuplicateInput } from '@/services';
import LearnMoreFooter from '@/shared/components/layouts/footers/LearnMoreFooter';
import type { LifecycleInitiative } from '@/shared/components/lifecycles/domain';
import RenameResourceModal from '@/shared/components/modals/ResourceActionModals/01-RenameResourceModal';
import DeleteResourceModal from '@/shared/components/modals/ResourceActionModals/03-DeleteResourceModal';
import { toast } from '@/shared/components/toasts/PlatformToast';
import {
    getWorkspaceResourceStatusToggleLabel,
    WorkspaceResourceStatusActionIcon,
} from '@/shared/components/workspace-resources';
import { WorkspaceSubjectType } from '@/types';

import type { WorkspaceResourceActionMenuItem } from '../helpers';
import WorkspaceResourceActionsDropdown from '../WorkspaceResourceActionsDropdown';

interface LifecycleActionsDropdownProps {
    children: ReactNode;
    lifecycle: LifecycleInitiative;
    onAddCategory?: () => void;
    onConfigureActions?: () => void;
    onOpenChange?: (open: boolean) => void;
    open?: boolean;
    resourceActions?: ReturnType<typeof useLifecycleResourceActions>;
}

/** Lifecycle actions rendered through the same searchable shell as every primary workspace resource. */
const LifecycleActionsDropdown: FC<LifecycleActionsDropdownProps> = ({
    children,
    lifecycle,
    onAddCategory,
    onConfigureActions,
    onOpenChange,
    open,
    resourceActions,
}) => {
    const navigate = useNavigate();
    const access = useWorkspaceResourceAccess('lifecycles', lifecycle.id);
    const createAccess = useResourceActionAccess('lifecycles', 'create');
    const createLifecycle = useCreateLifecycle();
    const deleteLifecycle = useDeleteLifecycle();
    const updateLifecycle = useUpdateLifecycle(lifecycle.id);
    const internalResourceActions = useLifecycleResourceActions(lifecycle);
    const { favoritePending, statePending, toggleArchive, toggleFavorite, toggleLock } =
        resourceActions ?? internalResourceActions;
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [renameOpen, setRenameOpen] = useState(false);
    const resourcePath = `/lifecycles/${lifecycle.id}`;
    const duplicateLifecycle = async () => {
        const copy = await createLifecycle.mutateAsync(lifecycleDuplicateInput(lifecycle));

        navigate(`/lifecycles/${copy.id}`);
    };
    const items: WorkspaceResourceActionMenuItem[] = [
        {
            disabled: favoritePending,
            icon: lifecycle.favorited ? <TbStarOff size='1rem' /> : <TbStar size='1rem' />,
            key: 'favorited',
            label: lifecycle.favorited ? 'Remove from Favorites' : 'Add to Favorites',
            onClick: () => void toggleFavorite(),
            permission: 'update',
            section: 'state',
        },
        {
            disabled: statePending,
            icon: <WorkspaceResourceStatusActionIcon active={lifecycle.locked} status='locked' />,
            key: 'locked',
            label: getWorkspaceResourceStatusToggleLabel('locked', lifecycle.locked),
            onClick: () => void toggleLock(),
            permission: 'manage',
            section: 'state',
        },
        {
            disabled: statePending,
            icon: <WorkspaceResourceStatusActionIcon active={lifecycle.archived} status='archived' />,
            key: 'archived',
            label: getWorkspaceResourceStatusToggleLabel('archived', lifecycle.archived),
            onClick: () => void toggleArchive(),
            permission: 'manage',
            section: 'state',
        },
        {
            icon: <TbCategoryPlus size='1rem' />,
            key: 'addCategory',
            label: 'Add Task Category',
            onClick: onAddCategory,
            permission: 'update',
            section: 'primary',
        },
        {
            icon: <TbBolt size='1rem' />,
            key: 'configureActions',
            label: 'Configure Actions',
            onClick: onConfigureActions,
            permission: 'update',
            section: 'primary',
        },
        {
            icon: <TbLink size='1rem' />,
            key: 'copyLink',
            label: 'Copy Link',
            onClick: () => {
                void navigator.clipboard.writeText(`${window.location.origin}${resourcePath}`).then(
                    () =>
                        toast('Link Copied', {
                            description: lifecycle.label,
                            icon: <TbLink size='1rem' />,
                        }),
                    (error) => {
                        captureException(error);
                        toast('Error Copying Link', { description: lifecycle.label });
                    },
                );
            },
            permission: 'read',
            section: 'navigation',
        },
        {
            icon: <TbExternalLink size='1rem' />,
            key: 'openInNewWindow',
            label: 'Open in New Window',
            onClick: () => window.open(resourcePath, '_blank', 'noopener,noreferrer'),
            permission: 'read',
            section: 'navigation',
        },
        {
            icon: <TbEdit size='1rem' />,
            key: 'rename',
            label: 'Rename',
            onClick: () => setRenameOpen(true),
            permission: 'update',
            section: 'mutations',
        },
        {
            disabled: createAccess.disabled || createLifecycle.isPending,
            icon: <BiDuplicate size='1rem' />,
            key: 'duplicate',
            label: 'Duplicate',
            onClick: () => void duplicateLifecycle(),
            permission: 'read',
            section: 'mutations',
        },
        {
            danger: true,
            icon: <TbTrash size='1rem' />,
            key: 'delete',
            label: 'Delete',
            onClick: () => setDeleteOpen(true),
            permission: 'manage',
            section: 'danger',
        },
    ];

    return (
        <>
            <WorkspaceResourceActionsDropdown
                access={access}
                footer={<LearnMoreFooter topic='Lifecycle actions' variant='dropdown' />}
                items={items}
                onOpenChange={onOpenChange}
                open={open}
                resourceLabel='Lifecycle'
                subject={{
                    subjectId: lifecycle.id,
                    subjectLabel: lifecycle.label,
                    subjectType: WorkspaceSubjectType.LIFECYCLE,
                }}
            >
                {children}
            </WorkspaceResourceActionsDropdown>
            <RenameResourceModal
                onRename={(name) => updateLifecycle.mutateAsync({ name, revision: lifecycle.revision ?? 1 })}
                open={renameOpen}
                resource={lifecycle}
                setOpen={setRenameOpen}
                singularResource='lifecycle'
            />
            <DeleteResourceModal
                onDelete={() => deleteLifecycle.mutateAsync(lifecycle.id)}
                onDeleted={() => navigate('/lifecycles')}
                open={deleteOpen}
                resource={lifecycle}
                setOpen={setDeleteOpen}
                singularResource='lifecycle'
            />
        </>
    );
};

export default LifecycleActionsDropdown;
